import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Eye, FileText, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authFetch = (url, options = {}) => fetch(url, { credentials: "include", ...options });

const STATUS_STYLES = {
  draft: { label: "Draft", cls: "bg-stone-200 text-stone-600" },
  viewed: { label: "Viewed", cls: "bg-blue-100 text-blue-700" },
  accepted: { label: "Signed & Accepted", cls: "bg-emerald-100 text-emerald-700" },
};

const EMPTY = {
  customer: { name: "", business: "", billing_address: "", project_address: "", phone: "", email: "" },
  project_type: "residential",
  line_items: [{ description: "", qty: 1, unit_price: 0 }],
  notes: "",
};

export default function AdminQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/admin/quotes`);
    if (res.ok) setQuotes(await res.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ id: null, ...EMPTY, line_items: [{ description: "", qty: 1, unit_price: 0 }] });
  const startEdit = (q) =>
    setEditing({
      id: q.id,
      customer: { ...EMPTY.customer, ...q.customer },
      project_type: q.project_type || "residential",
      line_items: q.line_items?.length ? q.line_items : [{ description: "", qty: 1, unit_price: 0 }],
      notes: q.notes || "",
    });

  const setCust = (key, value) => setEditing((d) => ({ ...d, customer: { ...d.customer, [key]: value } }));
  const setItem = (i, key, value) =>
    setEditing((d) => ({ ...d, line_items: d.line_items.map((it, idx) => (idx === i ? { ...it, [key]: value } : it)) }));
  const addItem = () => setEditing((d) => ({ ...d, line_items: [...d.line_items, { description: "", qty: 1, unit_price: 0 }] }));
  const removeItem = (i) => setEditing((d) => ({ ...d, line_items: d.line_items.filter((_, idx) => idx !== i) }));

  const total = editing ? editing.line_items.reduce((s, it) => s + (parseFloat(it.qty) || 0) * (parseFloat(it.unit_price) || 0), 0) : 0;

  const save = async () => {
    if (!editing.customer.name.trim()) {
      toast.error("Add the customer name first.");
      return;
    }
    const items = editing.line_items
      .filter((it) => it.description.trim())
      .map((it) => ({ description: it.description.trim(), qty: parseFloat(it.qty) || 0, unit_price: parseFloat(it.unit_price) || 0 }));
    setSaving(true);
    try {
      const payload = { customer: editing.customer, project_type: editing.project_type, line_items: items, notes: editing.notes };
      const res = editing.id
        ? await authFetch(`${API}/admin/quotes/${editing.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) })
        : await authFetch(`${API}/admin/quotes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!res.ok) throw new Error("Save failed");
      const data = await res.json();
      const id = editing.id || data.id;
      toast.success("Quote saved.");
      setEditing(null);
      load();
      copyLink(id);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (id) => {
    const link = `${window.location.origin}/quote/${id}`;
    navigator.clipboard?.writeText(link).then(() => toast.success("Customer link copied — text or email it to them."));
  };

  const remove = async (id, number) => {
    if (!window.confirm(`Delete quote ${number}? This can't be undone.`)) return;
    const res = await authFetch(`${API}/admin/quotes/${id}`, { method: "DELETE" });
    if (res.ok) {
      toast.success("Quote deleted.");
      load();
    }
  };

  if (editing) {
    return (
      <div className="gold-ring-card rounded-xl p-6 sm:p-8" data-testid="quote-builder">
        <div className="flex items-center justify-between mb-6">
          <h2 className="font-display text-xl text-amber-900">{editing.id ? "Edit Quote" : "New Quote"}</h2>
          <button onClick={() => setEditing(null)} data-testid="quote-builder-cancel" className="text-stone-400 hover:text-amber-700"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <input className="input-dark" placeholder="Customer name *" value={editing.customer.name} onChange={(e) => setCust("name", e.target.value)} data-testid="quote-customer-name" />
            <input className="input-dark" placeholder="Business name (if applicable)" value={editing.customer.business} onChange={(e) => setCust("business", e.target.value)} data-testid="quote-customer-business" />
            <input className="input-dark" placeholder="Billing address" value={editing.customer.billing_address} onChange={(e) => setCust("billing_address", e.target.value)} data-testid="quote-customer-billing" />
            <input className="input-dark" placeholder="Project address" value={editing.customer.project_address} onChange={(e) => setCust("project_address", e.target.value)} data-testid="quote-customer-project" />
            <input className="input-dark" placeholder="Phone" value={editing.customer.phone} onChange={(e) => setCust("phone", e.target.value)} data-testid="quote-customer-phone" />
            <input className="input-dark" placeholder="Email" value={editing.customer.email} onChange={(e) => setCust("email", e.target.value)} data-testid="quote-customer-email" />
          </div>

          <div className="flex gap-3">
            {["residential", "commercial"].map((t) => (
              <button
                key={t}
                onClick={() => setEditing((d) => ({ ...d, project_type: t }))}
                data-testid={`quote-type-${t}`}
                className={`text-xs uppercase tracking-wider font-semibold px-4 py-2 rounded-full border transition-colors duration-300 ${
                  editing.project_type === t ? "bg-amber-600 text-white border-amber-600" : "border-amber-600/25 bg-white text-stone-500"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          <div>
            <p className="text-xs uppercase tracking-wider font-semibold text-stone-500 mb-2">Line Items</p>
            <div className="space-y-2">
              {editing.line_items.map((it, i) => (
                <div key={i} className="grid grid-cols-[1fr_72px_110px_36px] gap-2 items-center" data-testid={`quote-item-${i}`}>
                  <input className="input-dark" placeholder="Description (e.g. 2-car garage metallic epoxy)" value={it.description} onChange={(e) => setItem(i, "description", e.target.value)} data-testid={`quote-item-${i}-desc`} />
                  <input className="input-dark" type="number" min="0" step="1" placeholder="Qty" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} data-testid={`quote-item-${i}-qty`} />
                  <input className="input-dark" type="number" min="0" step="0.01" placeholder="Unit $" value={it.unit_price} onChange={(e) => setItem(i, "unit_price", e.target.value)} data-testid={`quote-item-${i}-price`} />
                  <button onClick={() => removeItem(i)} aria-label="Remove item" data-testid={`quote-item-${i}-remove`} className="text-stone-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <button onClick={addItem} data-testid="quote-add-item" className="mt-3 text-xs uppercase tracking-wider font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1.5">
              <Plus className="w-4 h-4" /> Add line item
            </button>
          </div>

          <textarea className="input-dark min-h-[80px] resize-y" placeholder="Notes for the customer (optional)" value={editing.notes} onChange={(e) => setEditing((d) => ({ ...d, notes: e.target.value }))} data-testid="quote-notes" />

          <div className="flex items-center justify-between border-t border-amber-600/15 pt-4">
            <p className="font-display text-2xl text-stone-900" data-testid="quote-total">Total: ${total.toLocaleString("en-US", { minimumFractionDigits: 2 })}</p>
            <button
              onClick={save}
              disabled={saving}
              data-testid="quote-save-btn"
              className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-3.5 rounded-md shadow-md transition-colors duration-300 disabled:opacity-60 flex items-center gap-2"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save & Copy Link
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-quotes-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-stone-500">{quotes.length} quote{quotes.length === 1 ? "" : "s"}</p>
        <button
          onClick={startNew}
          data-testid="quote-new-btn"
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-xs px-5 py-2.5 rounded-md shadow-md transition-colors duration-300 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" /> New Quote
        </button>
      </div>

      {quotes.length === 0 ? (
        <p className="text-stone-500 text-center py-20" data-testid="quotes-empty">No quotes yet. Build your first one and send the link to a customer.</p>
      ) : (
        <div className="space-y-3">
          {quotes.map((q) => {
            const style = STATUS_STYLES[q.status] || STATUS_STYLES.draft;
            return (
              <div key={q.id} className="gold-ring-card rounded-lg p-5 flex flex-wrap items-center gap-4" data-testid={`quote-row-${q.number}`}>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-stone-900">{q.number} — {q.customer?.name || "Unnamed"}</p>
                  <p className="text-sm text-stone-500 mt-0.5">
                    ${(q.total || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })} · {new Date(q.created_at).toLocaleDateString()}
                  </p>
                </div>
                <span className={`text-xs font-semibold px-3 py-1 rounded-full ${style.cls}`}>{style.label}</span>
                <div className="flex items-center gap-2">
                  <button onClick={() => window.open(`/quote/${q.id}?preview=1`, "_blank")} data-testid={`quote-preview-${q.number}`} aria-label="Preview as customer" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><Eye className="w-4 h-4" /></button>
                  <button onClick={() => copyLink(q.id)} data-testid={`quote-copy-${q.number}`} aria-label="Copy customer link" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><Copy className="w-4 h-4" /></button>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}/api/quotes/${q.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`quote-pdf-${q.number}`} aria-label="Download PDF" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><FileText className="w-4 h-4" /></a>
                  <button onClick={() => startEdit(q)} data-testid={`quote-edit-${q.number}`} aria-label="Edit quote" className="p-2 text-stone-400 hover:text-amber-700 transition-colors"><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(q.id, q.number)} data-testid={`quote-delete-${q.number}`} aria-label="Delete quote" className="p-2 text-stone-400 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
